import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchPost } from '../lib/posts';
import type { PublicPost } from '../data/types';
import { Frame } from '../components/Frame';
import { useKeptFinds } from '../components/FilmCard';
import { FilmTable, PieceCard } from '../components/FilmTable';
import { collectPieces, currentPieces, toPick, landingOf, slotOfIdea, type FilmPiece, type Part } from '../lib/pieces';
import { listMyPosts, type FeedPost } from '../lib/feed';
import { listCharacters, type Character } from '../lib/crew';
import { useLightbox } from '../components/Lightbox';
import { CrewFinds } from '../components/CrewFinds';
import { StoryboardTeaser } from '../components/StoryboardTeaser';
import { StoryReveal } from '../components/StoryPrint';
import { getStoryboard, markStoryOpened, storyOpened, storyWords, WORKING as STORY_WORKING, type Storyboard } from '../lib/storyboard';
import { useToast } from '../components/Toast';
import { RESET, getChallenge } from '../data/exercises';
import { CREDIT, slotsFor, whatItGrew, whyAFilm, type Slot } from '../data/film';
import { dreamLabels } from '../data/intro-copy';
import { IMAGINE, REVEAL } from '../data/imagine-copy';
import { loadProfile, saveProfile, currentDay } from '../lib/state';
import { shareLink, savePicture } from '../lib/share';
import { track } from '../analytics';
import { Darkroom, PrintOnPeg, PrintReveal } from '../components/Reveal';
import { markOpened, withoutHeld } from '../lib/reveal';
import { transition } from '../lib/transition';
import { markPosted } from '../lib/feed-flash';
import { SendStory, FRIEND_STORY } from '../components/SendStory';
import { CastAsk, CastFlow } from '../components/Cast';
import { declineCast, shouldAsk } from '../lib/cast';
import { KeepAsk } from '../components/KeepAsk';
import { keepAskDue } from '../lib/keep-ask';
import { isSaved } from '../lib/account';
import { useViewed } from '../lib/viewed';

interface RewardState { challengeId: string; headline: string; pngUrl?: string; day: number; postId?: string; shareUrl?: string; subject?: string; ideas?: string[]; /** what you wrote, split and routed (lib/pieces.ts partsIn) */ parts?: Part[]; /** Day 1's story, kept when the warm-up ended: its storyboard is a print of its own. For A Story About You Two, the post's own id. */ storyId?: string; /** A Story About You Two: who it is about, so the send can ask for them by name */ sendTo?: string }

/** Polls the story's storyboard while the crew develop it. `undefined` until the first answer. */
function useStory(storyId?: string) {
  const [sb, setSb] = useState<Storyboard | null | undefined>(undefined);
  useEffect(() => {
    if (!storyId) return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      const s = await getStoryboard(storyId).catch(() => undefined);
      if (stop) return;
      if (s !== undefined) setSb(s);
      if (s && !STORY_WORKING.includes(s.status)) return;
      if (++tries < 90) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [storyId]);
  return sb;
}

/** How long the darkroom waits for the story once the exercise's picture is dry: it began minutes earlier, so it is rarely behind. */
const STORY_GRACE_MS = 30_000;

/**
 * How long a story about you two keeps the darkroom before the send is offered
 * without it. Its storyboard only starts once the card is kept, so this is the
 * whole draw: transcribe, plan, page (usually well under a minute).
 */
const FRIEND_STORY_WAIT_MS = 150_000;

/** Polls the post until your crew's pictures land (a few minutes), then stops. `gaveUp` once it stops without them. */
function useRenders(postId?: string) {
  const [post, setPost] = useState<PublicPost | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!postId) return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      try { const p = await fetchPost(postId); if (!stop) setPost(p); if (p && p.renderStatus !== 'pending' && p.renderStatus !== 'none') return; } catch { /* keep polling */ }
      if (stop) return;
      // quick at first: at low quality a picture lands in ~12 s, and the print should hang the moment it does
      tries += 1;
      if (tries < 96) setTimeout(tick, tries < 30 ? 2000 : 5000); else setGaveUp(true);
    };
    tick();
    return () => { stop = true; };
  }, [postId]);
  return { post, gaveUp };
}

type Phase = 'developing' | 'lifting' | 'ready' | 'open' | 'seat' | 'film' | 'hero' | 'cast' | 'keep';

/** Long enough to see the crew start work, even when the picture is already there. */
const MIN_DARKROOM_MS = 2600;

/**
 * After a card, when the crew are drawing it (well/product/23, K37–K39):
 *
 * 1. developing — the darkroom: your words on a print that is still only colour.
 *    Keep playing leaves (the print waits on Home); staying is allowed.
 * 2. ready — the print hangs face-down, lit. It never opens by itself.
 * 3. open — your tap turns it over: the picture full screen, your words at its
 *    foot, a small Kraben. Day 1's second picture hangs next, if it has come.
 * 4. keep — your tap keeps it: Kraben snaps it with his phone and posts it to
 *    your feed, the picture zips down to the Feed tab (owner, 2026-09-28, K41
 *    G1), and it goes into its slot of the film without a screen of its own.
 *    Then straight to the road, where the stop you just did wears the picture.
 *
 * With no picture coming (crew off, nothing to draw, it failed, or you left
 * the darkroom) it is the old two: film — "You created something", what the
 * exercise was for and why a film — then hero with your card.
 *
 * Be in your story (well/product/32): when Day 1's story print opens, the way
 * on is "Want to be in it?". A name and a photo send the print back to the
 * tray; the idea print is turned over while the crew draw ("While they draw
 * you: one more print."), and the recast print, "Starring Sam", hangs last.
 * If it is not dry by then, the darkroom waits for it, and leaving it there
 * leaves it face-down on Home.
 *
 * Once, how you get back tomorrow (components/KeepAsk.tsx): the email, and
 * inside Facebook's browser the way to carry the road into Safari or Chrome.
 * Since the lean Day 1 (owner, 2026-10-30) it is asked when the first print is
 * tapped on the line, before it turns over: the picture is right there, so
 * there is a reason to give it. "Not now" turns it over all the same. When no
 * print hangs, it is asked on the way off, as before.
 *
 * Then the road. Your moment and the calendar (screens/Moment.tsx) are kept
 * for later, for when a reminder comes back as an incentive of its own. The film is the table's compact row
 * (components/FilmTable.tsx, K33), the same one Home shows, so what lands here
 * is what Home and the table hold: your idea, in the slot its exercise gives.
 */
export function Reward() {
  const nav = useNavigate();
  const state = (useLocation().state as RewardState | null) || null;
  const profile = loadProfile();
  const challengeId = state?.challengeId || Object.values(profile.days).slice(-1)[0]?.challengeId || RESET[profile.path][0];
  const c = getChallenge(challengeId);
  const day = state?.day ?? currentDay(profile);
  const { post, gaveUp } = useRenders(state?.postId);
  const story = useStory(state?.storyId);
  /**
   * A Story About You Two (owner, 2026-09-30): the storyboard is the one print,
   * and the send is on it. No still, no card printed in its place: a witness
   * post is never drawn, and the card is not the magic.
   */
  const friendStory = c?.id === FRIEND_STORY && !!state?.storyId;
  const storyDry = !!story && story.status === 'done' && !!story.pageUrl && !storyOpened(story.storyId);
  const storyComing = !!state?.storyId && (story === undefined || (!!story && STORY_WORKING.includes(story.status)));
  // fixed when the print is lifted, so a story that dries later waits on Home rather than reshuffling the deck
  const [withStory, setWithStory] = useState(false);
  const [storyWaitOver, setStoryWaitOver] = useState(false);
  const renders = post?.renders || [];
  const failed = post?.renderStatus === 'failed';
  // the crew start work on every card while they are on (owner, 2026-09-27): a picture of your words,
  // or, when nothing can be drawn (a photo with no words, a private exercise) or it did not come out, your own card, printed
  const expecting = !!state?.postId && profile.crew !== 'off';
  const [phase, setPhaseRaw] = useState<Phase>(expecting ? 'developing' : 'film');
  const setPhase = (p: Phase) => transition(() => setPhaseRaw(p));
  const [ownCard, setOwnCard] = useState(false);
  const [shown, setShown] = useState(0);
  const fromOpen = useRef(false);
  const waitStart = useRef(Date.now());
  const { open, lightbox } = useLightbox();
  const { toast, show } = useToast();
  const [busy, setBusy] = useState(false);
  // "Want to be in it?": asked once, on the story's open; a photo sent leaves the recast `pending` until the deck is done
  const [cast, setCast] = useState<'asking' | 'tag' | 'pending' | 'settled'>('asking');
  useViewed('reward', phase === 'cast' ? `cast_${cast}` : phase);
  const [castSb, setCastSb] = useState<Storyboard | null>(null);
  const plushPage = useRef<string | undefined>(undefined);

  // each beat starts at the top: the film screen is long, and the hero's headline must not open off-screen
  useEffect(() => { window.scrollTo(0, 0); }, [phase]);

  // the picture has come (or will not): the Moon lifts the print out, then it hangs face-down until you turn it over
  const nothingComing = post?.renderStatus === 'none' || failed || gaveUp;
  const cardUrl = post?.cardUrl && !post.cardUrl.startsWith('blob:') ? post.cardUrl : state?.pngUrl;
  useEffect(() => {
    if (phase !== 'developing' || friendStory) return;
    const stillDry = renders.length > 0 || (nothingComing && !!cardUrl);
    if (!stillDry && !(nothingComing && storyDry)) { if (nothingComing && !storyComing) setPhase('film'); return; }
    // the exercise's print is dry; the story's, begun minutes ago, gets a little longer to catch up
    if (storyComing && !storyWaitOver) { const g = setTimeout(() => setStoryWaitOver(true), STORY_GRACE_MS); return () => clearTimeout(g); }
    const wait = Math.max(0, MIN_DARKROOM_MS - (Date.now() - waitStart.current));
    const t = setTimeout(() => {
      if (!renders.length && stillDry) setOwnCard(true);
      setWithStory(storyDry);
      track('print_ready', { waitedMs: Date.now() - waitStart.current, of: renders.length ? 'crew' : stillDry ? 'card' : 'story', story: storyDry });
      setPhaseRaw('lifting');
    }, wait);
    return () => clearTimeout(t);
  }, [phase, friendStory, renders.length, nothingComing, cardUrl, storyDry, storyComing, storyWaitOver]);
  // a story about you two waits for its storyboard alone: dry, it hangs; failed or too short, the send is offered without it
  const [friendWaitOver, setFriendWaitOver] = useState(false);
  useEffect(() => {
    if (!friendStory || phase !== 'developing') return;
    if (storyDry) {
      const t = setTimeout(() => { setWithStory(true); track('print_ready', { waitedMs: Date.now() - waitStart.current, of: 'story', story: true }); setPhaseRaw('lifting'); }, Math.max(0, MIN_DARKROOM_MS - (Date.now() - waitStart.current)));
      return () => clearTimeout(t);
    }
    if ((story && !STORY_WORKING.includes(story.status)) || friendWaitOver) { setPhase('film'); return; }
    const t = setTimeout(() => setFriendWaitOver(true), Math.max(0, FRIEND_STORY_WAIT_MS - (Date.now() - waitStart.current)));
    return () => clearTimeout(t);
  }, [friendStory, phase, storyDry, story, friendWaitOver]);
  useEffect(() => {
    if (phase !== 'lifting') return;
    const t = setTimeout(() => setPhase('ready'), 850);
    return () => clearTimeout(t);
  }, [phase]);

  const kept = useKeptFinds();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [chars, setChars] = useState<Character[]>([]);
  useEffect(() => { listMyPosts(40).then(setPosts).catch(() => {}); listCharacters().then(setChars).catch(() => {}); }, []);
  const collection = useMemo(() => collectPieces(kept || [], chars, withoutHeld(posts), profile.pencilled), [kept, chars, posts, profile.pencilled]);
  const current = useMemo(() => currentPieces(collection, profile.film?.picks), [collection, profile.film?.picks]);
  // What lands, and where: fixed once the collection is known and the crew's
  // picture has come or been given up on. With a picture, it is the idea they
  // drew, in that idea's slot; otherwise the first of your parts whose slot is
  // empty (lib/pieces.ts landingOf). Never the card's headline or the
  // exercise's title: those are our words, and a slot with nothing of yours
  // stays blank (owner, 2026-09-21).
  const parts = useMemo(() => state?.parts ?? (state?.ideas && c ? state.ideas.map((text) => ({ text, slot: slotsFor(c)[0] })).filter((p) => p.slot) : []), [state, c]);
  const [landing, setLanding] = useState<{ slot: Slot; text: string; index: number; picture?: string } | null>(null);
  const slot = landing?.slot ?? null;
  const picture = landing?.picture;
  useEffect(() => {
    if (!kept || !c || landing || (phase !== 'film' && phase !== 'hero' && phase !== 'seat')) return;
    const r = renders.find((x) => !!x.url && !!x.idea?.trim());
    const mine = slotsFor(c);
    if (r && mine.length) { setLanding({ slot: slotOfIdea(parts, r.idea, mine[0]), text: r.idea.trim(), index: 0, picture: r.url }); return; }
    const l = landingOf(parts, current);
    if (l) setLanding({ slot: l.part.slot, text: l.part.text, index: l.index });
  }, [kept, c, landing, phase, renders, parts, current]);
  const landingPiece: FilmPiece | null = landing
    ? picture
      ? { id: state?.postId ? `render:${state.postId}` : 'landing', refId: state?.postId ?? 'landing', postId: state?.postId, slot: landing.slot, text: landing.text, title: `Your Day ${day} idea · drawn by your crew`, img: picture, source: 'render', at: Date.now() }
      : { id: state?.postId ? `card:${state.postId}:${landing.index}` : 'landing', refId: state?.postId ? `${state.postId}:${landing.index}` : 'landing', postId: state?.postId, slot: landing.slot, text: landing.text, title: `${c?.title ?? 'Day 1'} · Day ${day}`, source: 'card', at: Date.now() }
    : null;

  const [landed, setLanded] = useState(false);
  const [flying, setFlying] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const slotEls = useRef<Partial<Record<Slot, HTMLDivElement | null>>>({});

  /** Seat it for good: the table's pick for this slot, so Home and /film hold the same film (pencilled stays for the offline row). */
  const pencil = () => {
    if (!slot || !c || !landingPiece) return;
    const p = loadProfile();
    const publicCard = post?.cardUrl && !post.cardUrl.startsWith('blob:') ? post.cardUrl : undefined;
    saveProfile({
      ...p,
      pencilled: { ...(p.pencilled || {}), [slot]: { challengeId: c.id, title: c.title, headline: landingPiece.text, cardUrl: publicCard, pictureUrl: picture, postId: state?.postId, at: Date.now() } },
      film: { ...(p.film || {}), picks: { ...(p.film?.picks || {}), [slot]: toPick(landingPiece) } },
    });
    track('slotted', { slot, of: picture ? 'crew' : 'card' });
  };

  /** The hero picture sits big for a beat, then flies into its slot. */
  const fly = () => {
    const hero = heroRef.current;
    const target = slot ? slotEls.current[slot] : null;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!hero || !target || still || !hero.animate) { pencil(); setLanded(true); return; }
    const a = hero.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    setFlying(true);
    hero.animate(
      [{ transform: 'rotate(-3deg)' }, { transform: `translate(${dx * 0.35}px, ${dy * 0.2 - 30}px) scale(0.8) rotate(4deg)`, offset: 0.35 }, { transform: `translate(${dx}px, ${dy}px) scale(${b.width / a.width}) rotate(0deg)` }],
      { duration: 900, easing: 'cubic-bezier(.55,0,.25,1)', fill: 'forwards' },
    ).finished.then(() => { pencil(); setLanded(true); }, () => { pencil(); setLanded(true); });
  };
  useEffect(() => {
    if (phase !== 'hero' || landed || flying) return;
    // seen full screen already when it came from the open: it goes in almost at once
    const t = setTimeout(fly, fromOpen.current ? 450 : 1700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, landed, flying]);

  const share = async () => {
    if (!state?.shareUrl) { show('Your crew is still working on it.'); return; }
    setBusy(true);
    try {
      const outcome = await shareLink(state.shareUrl, `${c?.title ?? 'Day 1'} · ${renders.length ? 'what my crew made from it' : 'made this today'}`);
      track('card_shared', { share_mode: renders.length ? 'picture_and_card' : 'share', outcome });
      if (outcome === 'copied') show('Link copied.');
      if (outcome === 'unsupported') show(`Could not open a share sheet here. The link: ${state.shareUrl}`);
    } finally { setBusy(false); }
  };
  const save = async () => {
    const url = picture || state?.pngUrl;
    if (!url) return;
    setBusy(true);
    try {
      const outcome = await savePicture(url, `kindling-day-${day}.${picture ? 'jpg' : 'png'}`);
      track('picture_saved', { outcome, of: picture ? 'crew' : 'card' });
      if (outcome === 'unsupported') show('Could not save that picture.');
    } finally { setBusy(false); }
  };
  // how the email ask went, when it was asked before the turn: the road says the link is on its way
  const link = useRef<'sent' | 'kept' | undefined>(undefined);
  // Straight to the road (lean Day 1, owner 2026-10-30): your moment and the calendar (screens/Moment.tsx)
  // are kept for later, when a reminder comes back as a reason of its own.
  const go = () => { track('reward_seen', { challengeId, renders: renders.length }); nav('/path', { viewTransition: true, state: { landed: challengeId, link: link.current } }); };
  // every way off the reward comes through here, so the ask is met whichever way the print went
  const onward = () => { if (keepAskDue(loadProfile(), isSaved())) { setPhase('keep'); return; } go(); };
  // The email is asked between the print on the line and its turn (lean Day 1): the picture is right there,
  // face-down, so there is a reason to give it. "Not now" turns it over all the same.
  const beforeTurn = useRef<(() => void) | null>(null);

  // kept and posted: seat it in the film quietly (Home and /film read the same picks), then the road.
  // The collection is usually known by now; if it is slow, the road does not wait for it.
  useEffect(() => {
    if (phase !== 'seat') return;
    if (landing && slot) { pencil(); onward(); return; }
    const t = setTimeout(onward, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, landing, slot]);

  // ── 1–3. the darkroom, the print, the turn ─────────────────────────────────
  const words = state?.ideas?.[0] || state?.headline || c?.title || '';
  // the imagine starter (well/product/33): the print is their first creation, and says so
  const first = c?.id === IMAGINE;
  // The deck: the story you told first (it was first), then the exercise's print(s).
  const stills = ownCard ? (cardUrl ? [{ url: cardUrl, idea: words }] : []) : renders;
  const deck: ({ kind: 'story' } | { kind: 'still'; url: string; idea: string })[] = [...(withStory && story ? [{ kind: 'story' as const }] : []), ...stills.map((r) => ({ kind: 'still' as const, url: r.url, idea: r.idea }))];
  const print = deck[shown];
  const prints = deck.length;
  const afterOpen = (castPending = cast === 'pending') => {
    // the next print hangs, if it has come; the rest wait on Home
    if (shown + 1 < prints) { setShown(shown + 1); setPhase('ready'); return; }
    // the recast is the last print: it hangs when dry, or the darkroom waits for it
    if (castPending) { setPhaseRaw('cast'); return; }
    finishDeck();
  };
  const finishDeck = () => {
    fromOpen.current = true;
    // kept: into the film without a screen of its own, then the road. An exercise that feeds no slot goes straight on.
    if (c && slotsFor(c).length) setPhaseRaw('seat');
    else onward();
  };
  if (phase === 'developing' || phase === 'lifting') {
    return <Darkroom day={day} words={words} lifting={phase === 'lifting'} {...(first ? { says: REVEAL.developing, label: REVEAL.label } : {})} onLeave={() => { track('reward_skipped_wait', { waitedMs: Date.now() - waitStart.current }); setPhase('film'); }} />;
  }
  if (phase === 'ready' && print) {
    return (
      <PrintOnPeg
        day={day}
        words={print.kind === 'story' ? storyWords(story!) : print.idea || ''}
        count={prints - shown}
        line={cast === 'pending' ? 'While they draw you: one more print.' : first && prints === 1 ? REVEAL.dry : undefined}
        onTurn={() => {
          const turn = () => {
            track('print_turned', { from: 'reward', index: shown, of: print.kind === 'story' ? 'story' : ownCard ? 'card' : 'crew' });
            if (print.kind === 'story') markStoryOpened(story!.storyId);
            else if (state?.postId && !ownCard) void markOpened(state.postId, print.url);
            setPhaseRaw('open');
          };
          if (keepAskDue(loadProfile(), isSaved())) { beforeTurn.current = turn; setPhase('keep'); return; }
          turn();
        }}
      />
    );
  }
  // the road Kraben's post ended on, held for the moment the film takes: never a blank screen
  if (phase === 'seat') return <div className="stage seat" aria-busy><img className="stage-bg" src="/art/road/road-bg.jpg" alt="" draggable={false} /></div>;
  if (phase === 'keep') {
    const turn = beforeTurn.current;
    return <KeepAsk day={day} beforeTurn={!!turn} onDone={(how) => {
      link.current = how;
      if (turn) { beforeTurn.current = null; turn(); } else go();
    }} />;
  }
  if (phase === 'cast' && castSb) return <CastFlow sb={castSb} start="casting" wipeFrom={plushPage.current} onDone={() => { setCast('settled'); finishDeck(); }} />;
  if (phase === 'open' && print?.kind === 'story' && story) {
    if (friendStory) {
      return <StoryReveal sb={story} onDone={() => afterOpen()} doneLabel="Keep going" kicker={`Storyboarded by the Moon · you and ${state?.sendTo || 'a friend'}`}
        extra={<SendStory postId={state?.postId} friend={state?.sendTo} />} />;
    }
    if (cast === 'tag') {
      return <CastFlow sb={story} start="tag" wipeFrom={story.pageUrl}
        onInterlude={(s) => { plushPage.current = story.pageUrl; setCastSb(s); setCast('pending'); afterOpen(true); }}
        onDone={() => { setCast('settled'); afterOpen(false); }} />;
    }
    const ask = cast === 'asking' && shouldAsk(story, profile)
      ? <CastAsk onYes={() => setCast('tag')} onNo={() => { declineCast(); setCast('settled'); afterOpen(false); }} />
      : undefined;
    return <StoryReveal sb={story} onDone={() => afterOpen()} doneLabel={shown + 1 < prints ? 'Next print' : 'Keep going'} ask={ask} />;
  }
  if (phase === 'open' && print?.kind === 'still') {
    return (
      <PrintReveal
        day={day}
        url={print.url}
        words={print.idea || ''}
        credit={ownCard ? `Your card · printed by the Moon · Day ${day}` : first ? REVEAL.credit(day) : c?.input.kind === 'whoami' ? 'You · drawn by the Moon · kept, so the crew can draw you again' : undefined}
        onPost={() => { markPosted(print.url); track('posted_by_kraben', { of: ownCard ? 'card' : 'crew' }); }}
        onDone={() => afterOpen()}
      />
    );
  }

  const base = { ...current };
  if (slot) delete base[slot];
  const pieces = landed && slot && landingPiece ? { ...base, [slot]: landingPiece } : base;
  const grew = c ? whatItGrew(c) : { brief: '', more: '' };
  const film = whyAFilm(profile.dreams);
  const said = film.said ? dreamLabels([film.said])[0] : undefined;

  // ── the friend story with no storyboard to show: send the recording ────────
  if (phase === 'film' && c?.id === FRIEND_STORY) {
    const drawing = !!story && STORY_WORKING.includes(story.status);
    return (
      <Frame left={<span className="tag">Day {day}</span>}>
        <div className="stack rise" style={{ gap: 10 }}>
          <h1>{drawing ? 'Your crew are still drawing it.' : 'You told a story about the two of you.'}</h1>
          <p className="lede">{[grew.brief, grew.more].filter(Boolean).join(' ')}</p>
          {drawing && <p className="why">It will wait on Home, face-down, and you can send it from there once it is dry. Or send your voice now: the link picks the pictures up when they land.</p>}
          {!drawing && story?.status === 'too-short' && <p className="why">A little short for four panels this time, so it goes as you told it.</p>}
        </div>
        <SendStory tone="sheet" postId={state?.postId} friend={state?.sendTo} drawn={false} />
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={onward}>Next</button>
        </div>
        {toast}
      </Frame>
    );
  }

  // ── 2. what that was for ───────────────────────────────────────────────────
  if (phase === 'film') {
    return (
      <Frame left={<span className="tag">Day {day}</span>}>
        <div className="stack rise" style={{ gap: 10 }}>
          <h1>You created something.</h1>
          <p className="lede">{[grew.brief, grew.more].filter(Boolean).join(' ')}</p>
          <p className="lede rise" style={{ animationDelay: '.2s' }}>Every creation is something you can draw on later: a person, a place, a want, a look. Here, we use your creations to make a <em>short</em> film.</p>
        </div>
        <div className="rise" style={{ marginTop: 30, animationDelay: '.45s' }}>
          <FilmTable mode="compact" pieces={collection} current={pieces} onPick={() => {}} next={slot} />
        </div>
        <p className="lede rise" style={{ animationDelay: '.7s' }}>
          {said
            ? <>Putting your creations together is part of becoming <b>{said.toLowerCase()}</b>, whether you knew it or not.</>
            : 'Putting your creations together is how a film gets made.'}
        </p>
        <div className="stack" style={{ marginTop: 'auto' }}>
          {slot && landingPiece
            ? <button className="btn" onClick={() => { track('film_explained', { slot }); setPhase('hero'); }}>Put it in my film</button>
            : <button className="btn" onClick={onward}>Next</button>}
        </div>
        {toast}
      </Frame>
    );
  }

  // ── 3. the hero: the picture goes in ───────────────────────────────────────
  const waitingNote = expecting && !picture && !ownCard
    ? failed
      ? post?.renderBlocked === 'billing' ? 'Your crew are out of ink just now, so your card went in. The picture will come when they are back.' : 'The drawing did not come out this time, so your card went in.'
      : post?.renderStatus === 'pending' || !post ? 'Your card went in while the crew finish. Their print will be waiting on Home, face-down, until you turn it over.' : null
    : null;
  return (
    <Frame left={<span className="tag">Day {day}</span>}>
      <div className="stack" style={{ gap: 4 }}>
        <span className="label" style={{ color: 'var(--green-dark)' }}>{slot ? `Your first film · ${CREDIT[slot].label.toLowerCase()}` : 'Your first film'}</span>
        <h1 key={landed ? 'in' : 'out'} className="rise">{landed && slot ? CREDIT[slot].line : picture ? 'Your crew made this from your idea.' : 'This is going in.'}</h1>
      </div>
      {!landed && landingPiece && (
        <div className="hero-wrap">
          <div ref={heroRef} className="hero-pic" onClick={() => { if (landingPiece.img) open(landingPiece.img, landingPiece.text); }}>
            {landingPiece.img ? <img src={landingPiece.img} alt={landingPiece.text} /> : <PieceCard piece={landingPiece} slot={landingPiece.slot} size="hand" />}
          </div>
          {!flying && <span className="hint rise" style={{ animationDelay: '.4s' }}>{picture ? `Drawn by your crew from “${landingPiece.text}”` : c?.title}</span>}
        </div>
      )}
      <div className="flown" style={{ marginTop: landed ? 44 : 30 }}>
        <FilmTable mode="compact" pieces={collection} current={pieces} onPick={() => {}} next={landed ? null : slot} landing={landed ? slot : null} slotRef={(s, el) => { slotEls.current[s] = el; }} onOpen={landed ? () => nav('/film') : undefined} />
      </div>
      {landed && (
        <div className="stack rise" style={{ gap: 12 }}>
          {waitingNote && <p className="hint" style={{ textAlign: 'center' }}>{waitingNote}</p>}
          <p className="why" style={{ textAlign: 'center' }}>One piece in. Every exercise on the road can fill another.</p>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn small butter" style={{ flex: 1 }} onClick={share} disabled={busy}>{busy ? 'One moment' : 'Share it'}</button>
            <button className="btn small ghost" style={{ flex: 1 }} onClick={save} disabled={busy}>{picture ? 'Save the picture' : 'Save the card'}</button>
          </div>
          {!withStory && <StoryboardTeaser postId={state?.storyId ?? state?.postId} />}
          <CrewFinds waiting={!!state?.postId} />
        </div>
      )}
      <div className="stack" style={{ marginTop: 'auto' }}>
        {landed ? <button className="btn" onClick={onward}>Grow some more</button> : <button className="link" style={{ alignSelf: 'center' }} onClick={() => { if (!flying) fly(); }}>Put it in now</button>}
      </div>
      {lightbox}
      {toast}
    </Frame>
  );
}
